import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { z } from 'zod';

import { fetchCheckoutSettings, useAccountBilling, useInvoices } from '../../../../api/billing.js';
import { isPermissionDenied } from '../../../../api/call.js';
import { usePermissions } from '../../../../api/me.js';
import {
  CycleSummary,
  InvoicesTable,
  PlansTable,
  WebsiteCard,
  WebsiteWarnings,
} from '../../../../billing/BillingSections.js';
import { CardsCard } from '../../../../billing/CardsCard.js';
import styles from '../../../../billing/billing.module.css';
import { openPaddleTransaction, returnUrl } from '../../../../billing/paddle-checkout.js';
import { DiscountCodeCard } from '../../../../billing/DiscountCodeCard.js';
import { Button } from '../../../../components/Button.js';
import { CardCarousel } from '../../../../components/CardCarousel.js';
import { DangerAlert } from '../../../../components/DangerAlert.js';
import { PageHeading } from '../../../../components/PageHeading.js';
import { LoadingArea } from '../../../../components/PlaceholderPage.js';
import { PermissionDeniedPage } from '../../../../components/SectionStates.js';

/**
 * `/account/billing`: billing for the whole account, not per website
 * (`product/business-rules.md`, Billing). This cycle's total so far, each
 * website's plan and usage, the plans, and the account's invoices: one per
 * cycle, issued on the 5th, covering every website; and the cards invoices are
 * charged to. Paddle's checkout for a card sends the payer back with `?card=added`.
 * It is also Paddle's default payment link: a link Paddle sends (e.g. to update
 * a card) arrives with `?_ptxn=<transaction>`, and the page opens that checkout.
 *
 * Reading order: anything that needs attention, then what this cycle costs so
 * far beside the cards it is charged to and the discount code form, then the
 * invoices, then each website's plan and usage, then the plans. Cards and
 * Discount code are paired at matching width next to the cycle card, because
 * either alone across the page's full width left most of its row empty.
 */
/** Website cards shown side by side before the list becomes a carousel. */
const WEBSITES_PER_VIEW = 3;

const searchSchema = z.object({
  card: z.enum(['added']).optional(),
  _ptxn: z
    .string()
    .regex(/^txn_[a-z\d]{26}$/)
    .optional(),
});

export const Route = createFileRoute('/_app/account/billing/')({
  validateSearch: (search: Record<string, unknown>) => {
    const parsed = searchSchema.safeParse(search);
    return parsed.success ? parsed.data : {};
  },
  component: BillingRoute,
});

const TITLE = 'Billing';
const PERMISSION = 'billing and invoices';

function BillingRoute() {
  const permissions = usePermissions();
  if (!permissions.includes('billing_and_invoices')) {
    return <PermissionDeniedPage title={TITLE} permission={PERMISSION} />;
  }
  return <BillingPage />;
}

function BillingPage() {
  const search = Route.useSearch();
  const returnedFromCheckout = search.card === 'added';
  const linkError = usePaddleLink(search._ptxn);
  const billing = useAccountBilling({ awaitingCard: returnedFromCheckout });
  const invoices = useInvoices();

  if (
    (billing.isError && isPermissionDenied(billing.error)) ||
    (invoices.isError && isPermissionDenied(invoices.error))
  ) {
    return <PermissionDeniedPage title={TITLE} permission={PERMISSION} />;
  }

  return (
    <>
      <PageHeading documentTitle={TITLE}>{TITLE}</PageHeading>
      <p className={styles.caption}>
        Billing for your whole account. Each website has its own plan; one invoice covers them all.
      </p>
      {linkError ? (
        <DangerAlert title="Couldn't open the payment page">{linkError}</DangerAlert>
      ) : null}
      <div className={styles.page}>
        {billing.isError || invoices.isError ? (
          <DangerAlert
            title="Couldn't load billing"
            action={
              <Button
                onClick={() => {
                  void billing.refetch();
                  void invoices.refetch();
                }}
              >
                Try again
              </Button>
            }
          >
            Check your connection and try again.
          </DangerAlert>
        ) : billing.isPending || invoices.isPending ? (
          <LoadingArea />
        ) : (
          <>
            {/* The row is hidden while no website has a warning (`:empty`). */}
            <div className={styles.alerts}>
              {billing.data.websites.map((website) => (
                <WebsiteWarnings key={website.websiteId} website={website} />
              ))}
            </div>
            <div className={styles.topGrid}>
              <CycleSummary billing={billing.data} />
              <div className={styles.payRow}>
                <CardsCard billing={billing.data} returnedFromCheckout={returnedFromCheckout} />
                <DiscountCodeCard billing={billing.data} />
              </div>
            </div>
            <InvoicesTable invoices={invoices.data.items} />
            {billing.data.websites.length === 0 ? (
              <p className={styles.muted}>Add a website to see its plan and usage here.</p>
            ) : (
              <section className={styles.section} aria-label="Websites">
                <h2 className={styles.sectionTitle}>Websites</h2>
                {/* Up to three side by side; past that, a carousel of three at a time. */}
                {billing.data.websites.length > WEBSITES_PER_VIEW ? (
                  <CardCarousel label="Websites" perView={WEBSITES_PER_VIEW}>
                    {billing.data.websites.map((website) => (
                      <WebsiteCard key={website.websiteId} website={website} />
                    ))}
                  </CardCarousel>
                ) : (
                  <div className={styles.cards}>
                    {billing.data.websites.map((website) => (
                      <WebsiteCard key={website.websiteId} website={website} />
                    ))}
                  </div>
                )}
              </section>
            )}
            <PlansTable billing={billing.data} />
          </>
        )}
      </div>
    </>
  );
}

/**
 * A checkout link Paddle sent (`?_ptxn=`): the transaction comes off the
 * address, so a reload doesn't open it again, and its checkout opens over the
 * page. Returns why it couldn't, if it couldn't.
 */
function usePaddleLink(transactionId: string | undefined): string | null {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const opened = useRef(false);
  useEffect(() => {
    if (transactionId === undefined || opened.current) return;
    opened.current = true;
    void navigate({
      to: '/account/billing',
      search: (prev) => ({ ...prev, _ptxn: undefined }),
      replace: true,
    });
    fetchCheckoutSettings()
      .then(({ settings }) => {
        if (settings === null) throw new Error('Card payments are not available.');
        return openPaddleTransaction(settings, transactionId, returnUrl('card', 'added'));
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, [transactionId, navigate]);
  return error;
}
